import { Group, useMantineTheme, Flex } from '@mantine/core';
import { upperFirst } from '@mantine/hooks';
import React, { useCallback, useMemo } from 'react';
import type { ContestSource } from '@betterpool/api-types/contests-service';
import { ContestStatus } from '@betterpool/api-types/contests-service';
import useTranslation from 'next-translate/useTranslation';

import ContestCountDown from '~/components/utils/ContestCountdown';
import { BadgeIcons } from '~/components/shared/Badges/Badges';
import { Avatar } from '~/domains/design-system/Avatar';
import { LinkNew } from '~/domains/common/components/Link/Link';
import { Routes } from '~/domains/common/constants/routes';
import type { Contest } from '~/domains/contest/domains/services/interfaces/contest.interface';
import type { MyUserContest } from '~/domains/contest/domains/myContests/types/userContest';
import { Text } from '~/domains/design-system/Text';
import { TruncateText } from '~/domains/design-system/TruncateText';
import { useInvitesManagerEnabled } from '~/domains/invites-manager/hooks/useInvitesManagerEnabled';
import dt from '~/testing';

import { useContestCardCountdownDisplay } from '../../hooks/useContestCardCountdownDisplay';
import ContestTypeBadge from '../ContestTypeBadge/ContestTypeBadge';
import { getContestThumbnail } from '../../utils/getContestThumbnail';

import ContestCardMenu from './ContestCardMenu';
import useStyles from './ContestCard.styles';

type ContestCardHeaderProps = {
  contest: Contest | MyUserContest;
  isContestThumbnailShown?: boolean;
  isBadgeIconSectionEnabled?: boolean;
  isCardMenuVisible?: boolean;
  isCommissionerLinkEnabled?: boolean;
  isCommissioner?: boolean;
  badgeName?: ContestSource;
};

function ContestCardHeader({
  contest,
  isContestThumbnailShown,
  isBadgeIconSectionEnabled = true,
  isCardMenuVisible = true,
  isCommissionerLinkEnabled = true,
  isCommissioner,
  badgeName,
}: ContestCardHeaderProps) {
  const theme = useMantineTheme();
  const { t } = useTranslation('contest');
  const { classes } = useStyles({});
  const isEntryDeadlinePending = contest?.is_entry_deadline_pending;
  const { isInvitesManagerEnabled } = useInvitesManagerEnabled();
  const {
    contestDates,
    getLockTimeColor,
    getLockTimeText,
    getFinalizedDate,
    getEntryLockTimeColor,
  } = useContestCardCountdownDisplay({ contest });

  // Fallback to league sport if no tags are present (usually for legacy contests)
  const contestTags = useMemo(() => {
    if (contest.tags?.length > 0) return contest.tags;

    return [contest.league.sport];
  }, [contest.tags, contest.league.sport]);

  const leagueDisplayName = useMemo(() => {
    const v2Leagues = (contest as MyUserContest).leagues;
    if (v2Leagues?.length) {
      return v2Leagues.map((league) => league.name).join(' + ');
    }

    return contest.league?.name;
  }, [contest]);

  const renderLockTimeDisplay = useCallback(() => {
    if (!contestDates.lockDate) return null;
    return (
      <>
        <Text component="span" color={theme.colors.gray[6]} size={theme.fontSizes.xs} fw={500}>
          {getLockTimeText()}
        </Text>
        <ContestCountDown
          color={getLockTimeColor()}
          fontSize={theme.fontSizes.xs}
          contestStart={contestDates.lockDate}
          fontWeight={500}
        />
      </>
    );
  }, [contestDates.lockDate, getLockTimeColor, getLockTimeText, theme]);
  return (
    <Group position="apart" className={classes.top}>
      <Group noWrap className={classes.headerWrapper}>
        {isContestThumbnailShown && (
          <Avatar
            // TODO: update username to handle
            handle={contest.commissioner.handle || contest.commissioner.username}
            size={48}
            src={getContestThumbnail({
              contestThumbnailUrl: contest.thumbnail_url,
              createdByAdmin: contest.created_by_admin,
              avatarUrl: contest.commissioner.avatarUrl,
            })}
            badge={badgeName}
          />
        )}

        <div className={classes.titleWrapper}>
          <TruncateText
            data-test-id={dt.common.components.contestCardHeader}
            variant="headline-medium"
            fw={800}
            w="100%"
            tooltipLabel={upperFirst(contest.name)}
          >
            {upperFirst(contest.name)}
          </TruncateText>
          <Flex gap={theme.other.spacing._4} wrap={{ base: 'wrap' }} align="center">
            {contest.commissioner && !isCommissioner ? (
              <>
                {isCommissionerLinkEnabled ? (
                  <LinkNew
                    onClick={(e) => e.stopPropagation()}
                    href={Routes.channel(contest.commissioner.handle)}
                    sx={{ color: theme.colors.gray[6] }}
                  >
                    <Text
                      component="span"
                      size={theme.fontSizes.xs}
                      display="block"
                      truncate
                      fw="bold"
                    >
                      {contest.commissioner.handle || contest.commissioner.username || 'Unnamed'}
                    </Text>
                  </LinkNew>
                ) : (
                  <Text component="span" size={theme.fontSizes.xs} display="block" truncate>
                    {contest.commissioner.handle || contest.commissioner.username
                      ? `@${contest.commissioner.handle || contest.commissioner.username}`
                      : 'Unnamed'}
                  </Text>
                )}
                <Text color={theme.colors.gray[6]} variant="body-small">
                  •
                </Text>
              </>
            ) : null}

            <Text color={theme.colors.gray[6]} variant="body-small">
              {(() => {
                switch (contest.status) {
                  case ContestStatus.SCHEDULED: {
                    if (isEntryDeadlinePending) {
                      return t('common.contestCard.header.firstTeeTimeTbd');
                    }
                    if (!contestDates.entryLockDate) return null;

                    return (
                      <ContestCountDown
                        color={getEntryLockTimeColor()}
                        fontSize={theme.fontSizes.xs}
                        contestStart={contestDates.entryLockDate}
                        fontWeight={500}
                      />
                    );
                  }
                  case ContestStatus.IN_PROGRESS:
                    if (isCommissioner) {
                      return (
                        <Flex align="center" gap={theme.other.spacing._4}>
                          <div className={classes.liveDot} />
                          <Text
                            component="span"
                            color={theme.colors.gray[7]}
                            size={theme.fontSizes.xs}
                            fw={500}
                          >
                            Live
                          </Text>
                        </Flex>
                      );
                    }
                    return renderLockTimeDisplay();

                  case ContestStatus.FINISHED:
                    return renderLockTimeDisplay();
                  case ContestStatus.FINALIZED: {
                    const finalizedDate = getFinalizedDate();
                    if (!finalizedDate) return null;

                    return (
                      <>
                        <Text
                          component="span"
                          color={theme.colors.gray[6]}
                          size={theme.fontSizes.xs}
                          fw={500}
                        >
                          Final{' '}
                        </Text>
                        <ContestCountDown
                          color={theme.colors.gray[6]}
                          fontSize={theme.fontSizes.xs}
                          fontWeight={500}
                          contestStart={finalizedDate}
                          dateFormat="M/d/yy"
                        />
                      </>
                    );
                  }
                  default: {
                    return null;
                  }
                }
              })()}
            </Text>
            <Text color={theme.colors.gray[6]} variant="body-small">
              •
            </Text>
            {isBadgeIconSectionEnabled ? (
              <BadgeIcons
                wrapStyle={undefined}
                tags={contestTags}
                iconSize={16}
                iconColor={theme.colors.gray[6]}
              />
            ) : (
              leagueDisplayName &&
              leagueDisplayName !== 'unknown' && (
                <Text color={theme.colors.gray[6]}>{leagueDisplayName}</Text>
              )
            )}
            <ContestTypeBadge
              type={contest.contest_type}
              altLabel={contest.contest_type_alt_text || contest.contestTypeAltText}
              variant="text"
              color={theme.colors.gray[6]}
            />
          </Flex>
        </div>
      </Group>
      {!isInvitesManagerEnabled && isCardMenuVisible ? (
        <div data-ignore-link className={classes.menu}>
          <ContestCardMenu contest={contest} />
        </div>
      ) : null}
    </Group>
  );
}

export default ContestCardHeader;
